<script>
import partnerNames from '@/mixins/partnerNames';
import {hasEditPermission, currentRoleLevelName} from '@/mixins/roleLevelPermissions';
import { mapState } from 'vuex';

export default {
	mixins: [partnerNames],
	props: ['agreementData'],
	data() {
		return { 	
			isViewMode : !hasEditPermission('section-child-support'),
			showLearnButton: ['client-level-1', 'client-level-2'].includes(currentRoleLevelName)
		};	
	},
	computed: {
		...mapState(['currentRoleLevel']),
		childSupportExpenses() {
			return this.agreementData.childSupportExpenses;
			// return this.agreementData.childSupportExpenses =  this.agreementData.childSupportExpenses.hasOwnProperty('additionalClausesToRecitals') 
			// 	? this.agreementData.childSupportExpenses 
			// 	: {...this.agreementData.childSupportExpenses, additionalClausesToRecitals: []};
		},
		isUserSeparatingParty(){
			return this.currentRoleLevel.roleAssociated === 'party'
		},
		dynamicQuestions(){
			if(this.isUserSeparatingParty){
				return {
					seeOptions: "What types of special or extraordinary expenses will you and the other parent share?",
					childCareOptions: "Will you like to included childcare during holidays or school breaks? ",
					seeAccounting: "Will you conduct accounting of special or extraordinary expenses each year to ensure the correct amount has been paid by the parties?"

				}
			}
			else{
				return {
					seeOptions: "What types of special or extraordinary expenses will the parties share?",
					childCareOptions: "Will the parties use any child care during holidays?",
					seeAccounting : "Will the parties conduct accounting of special or extraordinary expenses each year to ensure the correct amount has been paid by the parties?"

				}
			}
		}
	},
	mounted(){
		this.agreementData.childSupportExpenses =  this.agreementData.childSupportExpenses.hasOwnProperty('additionalClausesToRecitals') 
			? this.agreementData.childSupportExpenses 
			: {...this.agreementData.childSupportExpenses, additionalClausesToRecitals: []};
	}
};
</script>

<template>
	<b-card>
		<div slot="header" class="section-heading">
			<h4>Special or Extraordinary Expenses</h4>
			<a href="#" onclick="Intercom('showArticle', 11106777)" v-if="this.showLearnButton">Learn &nbsp;<b-icon-book/></a>
		</div>

		<conditional-check-group
			v-model="childSupportExpenses.seeOptions"
			class="mb-3"
			:label="dynamicQuestions.seeOptions"
			:options="[
				{ value: 'Child Care' ,tooltip:'Childcare expenses incurred as a result of the employment, illness, disability or education or training for employment of the spouse who has the majority of parenting time'},
				{ value: 'Health', tooltip: `health-related expenses include that portion of the medical and dental insurance premiums attributable to the child; and expenses that exceed insurance reimbursement by at least $100 annually,
					including orthodontic treatment, professional counselling provided by a psychologist, social worker, psychiatrist or any other person, physiotherapy, occupational therapy, speech therapy and prescription drugs, hearing aids, glasses and contact lenses` },
				{ value: 'Extracurriculars', tooltip: 'extraordinary expenses for extracurricular activities' },
				{ value: 'Educational', tooltip: 'extraordinary expenses for primary or secondary school education or for any other educational programs that meet the child’s particular needs and expenses for post-secondary education' },
			]"
			:testid="'special-expense-type-'"
			:id="'special-expense-type-'"
			:disabled="isViewMode"
			toolTipText="Under the <i>Federal Child Support Guidelines</i>, parents typically share special or extraordinary expenses on top of the table amount of Child Support payable.  "
		>
			<template slot="Child Care">
				<check-group
					v-model="childSupportExpenses.childCareOptions"
					:options="[
						{ value: 'day care', text: 'Day care' }, // value fix harder
						{ value: 'camps', text: 'Camps' },
					]"
					:label="dynamicQuestions.childCareOptions"
					other
					@reset="childSupportExpenses.childCareOptions = []"
					:testid="'special-expense-child-care-options'"
					:id="'special-expense-child-care-options'"
					:disabled="isViewMode"	
				/>
			</template>

			<template slot="Health">
				<yes-no-choice
					v-model="childSupportExpenses.seeHealthShared"
					reset-undefined
					label="Would you like to set a threshold amount after which health care expenses will be shared?"
					:testid="'special-expense-set-threshold-amount-after-health-care-expenses'"
					:disabled="isViewMode"
					toolTipText="A threshold amount means that you wouldn't share small expenses, only ones over and above the threshold amount.
					eg. health-related expenses that exceed insurance reimbursement by a combined total of at least $200.00 annually"
				/>

				<conditional-group
					class="mt-2"
					:condition="childSupportExpenses.seeHealthShared"
				>
					<input-group
						v-model="childSupportExpenses.seeHealth"
						label="What is the threshold amount?"
						reset-undefined
						dollar
						:testid="'special-expense-threshold-amount'"
						:disabled="isViewMode"
					/>
				</conditional-group>
			</template>

			<template slot="Extracurriculars">
				<yes-no-choice
					v-model="childSupportExpenses.seeExtracurricularsShared"
					reset-undefined
					label="Would you like to set a threshold amount after which extracurricular expenses will be shared?" 
					:testid="'special-expense-set-threshold-amunt-after-extracurricualr-expenses'"
					:disabled="isViewMode"
					toolTipText="A threshold amount means that you wouldn't share small expenses, only ones over and above the threshold amount.
					eg. extracurricular activities exceeding $100.00 per year"
					/>			

				<conditional-group
					class="mt-2"
					:condition="childSupportExpenses.seeExtracurricularsShared"
				>
					<input-group
						v-model="childSupportExpenses.seeExtracurriculars"
						label="What is the threshold amount?"
						reset-undefined
						dollar
						:testid="'special-expense-threshold-amount-extracurriculars'"
						:disabled="isViewMode"
					/> </conditional-group
			></template>
		</conditional-check-group>

		<input-group
			v-model="childSupportExpenses.partner1Proportion"
			:label="`${partner1Name}'s Proportion of special or extraordinary expenses`"
			class="mb-3"
			input-cols="2"
			percentage
			reset-undefined
			mobileWidth="6"
			:testid="'special-expense-p1-proportion'"
			:disabled="isViewMode"
			toolTipText="The guiding principle under the <i>Federal Child Support Guidelines</i> is that the expense is shared in proportion to your incomes.
			Your child support calculation will indicate what proportion you should each pay.
			You may also agree to an alternative proportion, such as 50-50%"
		/>

		<input-group
			v-model="childSupportExpenses.partner2Proportion"
			:label="`${partner2Name}'s Proportion of special or extraordinary expenses`"
			class="mb-3"
			input-cols="2"
			mobileWidth="6"
			percentage
			reset-undefined
			:testid="'special-expense-p2-proportion'"
			:disabled="isViewMode"
			toolTipText="The guiding principle under the <i>Federal Child Support Guidelines</i> is that the expense is shared in proportion to your incomes.
			Your child support calculation will indicate what proportion you should each pay.
			You may also agree to an alternative proportion, such as 50-50%"
		/>

		<yes-no-choice
			v-model="childSupportExpenses.seeAccounting"
			:label="dynamicQuestions.seeAccounting"
			:testid="'special-expense-conduct-accounting'"
			:disabled="isViewMode"
			toolTipText="It's wise to reconcile your expenses each year when you run a child support review."
		/>
		<additional-clauses :title="'Additional Clauses into Recitals'" :newLabel="'Add Clause to Recitals'" class="mt-2" v-model="childSupportExpenses.additionalClausesToRecitals" :testid="'specialorextraordinaryexpences-add-clause-to-recitals'"
		:disabled="isViewMode"/>
		<additional-clauses :title="'Additional Clauses into Sections'" :newLabel="'Add Clause to Section'" class="mt-2" v-model="childSupportExpenses.additionalClauses" :testid="'specialorextraordinaryexpences-add-clause-to'"
		:disabled="isViewMode"/>
	</b-card>
</template>
